/**
 * La génération au live du masteur (pur) : les règles des maquettes du 04/11, câblées (Wajih : « Go, pour
 * tous les lots »). Testées à côté ; le crochet du fil (`useTutorLiveFeedGeneration`) les lit.
 *
 *  - **La relecture s'ouvre seule** au centre si le tuteur attendait sur la même vue (le même élève regardé,
 *    ou le groupe) qu'à « Générer » ; sinon la carte « prêtes » garde une pastille et dit pendant qui elle
 *    est arrivée (`reviewOpensAlone`, `unseenWhile`). Arbitrage 1 du corps de l'issue.
 *  - **La ligne vivante** dit ce qui se passe vraiment : d'où part la génération (la photo, la consigne,
 *    l'activité dont on fait une semblable), les réglages repris, puis les propositions qui s'écrivent, une à
 *    une (`generationSteps`). Les deux premières étapes sont faites dès l'envoi : rien n'est inventé.
 *  - **Les commandes de « similaire »** (même niveau, plus facile, plus dur) sont une consigne de plus, envoyée à
 *    l'endpoint d'aujourd'hui (`instructions`, lu par les deux corps), jamais un nouvel appel (`withAdjustments`).
 *    Elles se touchent dans le champ ; la palette `/` les garde au clavier (`paletteCommands`, `filterCommands`).
 *  - **La proposition du champion est une remédiation** (Wajih, 04/11) : elle ne part que sur la correction d'un rendu
 *    où l'élève a beaucoup faux (`needsRemediation`), et c'est une « similaire » plus facile, ciblée sur ce qui est
 *    faux (`remediationInstruction`). Bornée : une à la fois par élève, une par élève toutes les 10 minutes, jamais
 *    quand le tuteur génère (`proposalAllowed`).
 */
import { MIN_INSTRUCTIONS_LENGTH } from "../../../adapters/parent/Activities/activityGenerationAdapter"
import { countLabel } from "../../../ui/formatters/textFormatter"
import { type UIGeneratedActivity } from "../../../ui/types"
import { type UIActivityCorrectionPointType } from "../../../ui/types/activityDetails"
import { type UITutorGenerationStep } from "../../../ui/types/tutorLive"

// ── La relecture qui s'ouvre seule ─────────────────────────────────────────────────────────────

/** La vue que le tuteur regarde : l'élève, ou `null` pour le groupe (la mosaïque, ou le filtre retiré). */
export type TutorGenerationView = string | null

/**
 * La relecture s'ouvre-t-elle seule quand la première proposition arrive ? Oui si le tuteur regarde la même vue
 * qu'à l'envoi, et qu'aucune présentation ne tourne (la scène se poserait sur ce que voient les élèves : seul un
 * geste du tuteur l'ouvre alors, comme toute boîte pendant une présentation).
 */
export function reviewOpensAlone(input: {
    sentFrom: TutorGenerationView
    watching: TutorGenerationView
    presenting: boolean
}): boolean {
    return !input.presenting && input.sentFrom === input.watching
}

/**
 * « Prête pendant que tu regardais Jules » : la phrase de la carte quand la relecture ne s'est pas ouverte seule.
 * `null` quand elle s'est ouverte, ou qu'on ne sait pas qui était regardé.
 */
export function unseenWhile(watchingName: string | null, openedAlone: boolean): string | null {
    if (openedAlone || watchingName == null) return null
    return `Prête pendant que tu regardais ${watchingName}`
}

// ── La ligne vivante ───────────────────────────────────────────────────────────────────────────

/** D'où part la génération : ce que la première étape dit. */
export type TutorGenerationSource = "photo" | "document" | "similar" | "text"

export type TutorGenerationStep = UITutorGenerationStep

const SOURCE_STEP: Record<TutorGenerationSource, (helper: string) => string> = {
    photo: (helper) => `${helper} lit la photo`,
    document: (helper) => `${helper} lit le document`,
    similar: (helper) => `${helper} reprend l'activité`,
    text: (helper) => `${helper} lit ta consigne`,
}

/** « 3 propositions », « 1 proposition prête sur 3 » : ce qui s'écrit, compté honnêtement. */
function writingLabel(results: Pick<UIGeneratedActivity, "status">[]): string {
    const total = results.length
    const arrived = results.filter((card) => card.status !== "pending").length
    if (arrived === 0) return `Écrit ${total} propositions`
    return `Écrit ${total} propositions · ${arrived} sur ${total}`
}

/**
 * Les étapes de la ligne vivante. La source et les réglages sont lus avant l'envoi : faits. L'écriture est en cours
 * tant qu'une proposition attend, faite quand toutes sont là.
 */
export function generationSteps(input: {
    source: TutorGenerationSource
    helperName: string
    /** « Fractions · 10 min » : les réglages repris, sans ce qui manque. */
    settingsLabel: string
    results: Pick<UIGeneratedActivity, "status">[]
}): TutorGenerationStep[] {
    const writing = input.results.some((card) => card.status === "pending")
    return [
        { label: SOURCE_STEP[input.source](input.helperName), state: "done" },
        {
            label:
                input.settingsLabel !== "" ? `Reprend les réglages : ${input.settingsLabel}` : "Reprend les réglages",
            state: "done",
        },
        { label: writingLabel(input.results), state: writing ? "active" : "done" },
    ]
}

/** Le titre de la carte qui annonce les propositions (« Ta demande · 3 propositions »), et celles encore en cours. */
export function readyTitle(results: Pick<UIGeneratedActivity, "status">[]): string {
    const ready = results.filter((card) => card.status === "ready").length
    const pending = results.filter((card) => card.status === "pending").length
    const head = `Ta demande · ${ready === 1 ? "1 proposition" : `${ready} propositions`}`
    return pending > 0 ? `${head} · ${pending} en cours` : head
}

/** La carte « prêtes » paraît dès qu'une proposition est là ; la ligne vivante tient jusque-là. */
export function generationPhase(
    results: Pick<UIGeneratedActivity, "status">[]
): "idle" | "writing" | "ready" | "failed" {
    if (results.length === 0) return "idle"
    if (results.some((card) => card.status === "ready")) return "ready"
    if (results.every((card) => card.status === "error")) return "failed"
    return "writing"
}

// ── Les commandes de « similaire » ────────────────────────────────────────────────────────────

/** Les commandes de « similaire » (même niveau, plus facile, plus dur) : une consigne, ou aucune. */
export const SIMILAR_MODIFIERS = [
    { key: "same", label: "Similaire", instruction: "" },
    { key: "easier", label: "Plus facile", instruction: "Un peu plus facile que l'original." },
    { key: "harder", label: "Plus dur", instruction: "Un peu plus difficile que l'original." },
] as const
export type TutorSimilarModifier = (typeof SIMILAR_MODIFIERS)[number]["key"]

/**
 * La consigne envoyée : celle du tuteur, puis la phrase de chaque ajustement ou raccourci, une fois chacune.
 * L'endpoint d'aujourd'hui lit `instructions` dans ses deux corps (le formulaire et les similaires).
 */
export function withAdjustments(base: string, additions: string[]): string {
    const parts = [base.trim()]
    for (const addition of additions) {
        const text = addition.trim()
        if (text !== "" && !parts.includes(text)) parts.push(text)
    }
    return parts.filter((part) => part !== "").join("\n")
}

export function modifierInstruction(key: TutorSimilarModifier): string {
    return SIMILAR_MODIFIERS.find((modifier) => modifier.key === key)?.instruction ?? ""
}

/** La consigne la plus longue qu'acceptent les similaires (`max:500` de leurs deux routes). */
export const MAX_SIMILAR_INSTRUCTIONS_LENGTH = 500

/**
 * Ce que le serveur refuserait dans la consigne, dit avant l'envoi : la génération du formulaire en veut cinq
 * caractères au moins quand il y en a une (`min:5`, chapitre choisi ou non), les similaires 500 au plus. Refusée,
 * chacune des trois propositions échouait sans dire pourquoi.
 */
export function instructionsRefusal(instructions: string, similar: boolean): "short" | "long" | null {
    const length = instructions.trim().length
    if (similar) return length > MAX_SIMILAR_INSTRUCTIONS_LENGTH ? "long" : null
    return length > 0 && length < MIN_INSTRUCTIONS_LENGTH ? "short" : null
}

// ── La palette « / » ───────────────────────────────────────────────────────────────────────────

export type TutorPaletteCommandKey = "similar" | "easier" | "harder" | "chapter" | "duration" | "level"

export type TutorPaletteCommand = { key: TutorPaletteCommandKey; label: string; hint: string }

/**
 * Les commandes du champ. « Similaire » et ses deux pentes n'ont de sens qu'avec une activité ouverte au centre
 * pour l'élève visé (`similarOf`, son titre) ; les réglages se règlent toujours.
 */
export function paletteCommands(similarOf: string | null): TutorPaletteCommand[] {
    const similar: TutorPaletteCommand[] =
        similarOf != null
            ? [
                  { key: "similar", label: "/similaire", hint: `Comme « ${similarOf} »` },
                  { key: "easier", label: "/plus facile", hint: "La même, une marche plus bas" },
                  { key: "harder", label: "/plus dur", hint: "La même, une marche plus haut" },
              ]
            : []
    return [
        ...similar,
        { key: "chapter", label: "/chapitre", hint: "Choisir le chapitre" },
        { key: "level", label: "/type", hint: "Choisir le type d'activité" },
        { key: "duration", label: "/durée", hint: "Choisir la durée" },
    ]
}

/** Sans accents ni casse : « /duree » trouve « /durée ». */
function folded(text: string): string {
    return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

/**
 * Les commandes qu'un texte tapé appelle : rien tant qu'il ne commence pas par « / » ; ensuite celles dont le
 * nom commence par ce qui suit (« /plus » : plus facile, plus dur).
 */
export function filterCommands(text: string, commands: TutorPaletteCommand[]): TutorPaletteCommand[] | null {
    if (!text.startsWith("/") || text.includes("\n")) return null
    const typed = folded(text.trim())
    return commands.filter((command) => folded(command.label).startsWith(typed))
}

// ── La proposition du champion, faite d'avance ──────────────────────────────────────────────────

/** Une proposition par élève toutes les 10 minutes, au plus. */
export const PROPOSAL_COOLDOWN_MS = 10 * 60 * 1000

/**
 * Une proposition peut-elle partir pour cet élève ? Jamais deux à la fois pour lui, jamais deux en 10 minutes,
 * jamais quand le tuteur génère (pour lui ou un autre : la génération du champ attend sa réponse, et la proposition
 * part par sa propre voie). Arbitrage 5 : la génération faite d'avance est bornée, et son coût mesuré.
 */
export function proposalAllowed(input: {
    studentId: string
    now: number
    inFlight: ReadonlySet<string>
    lastStartedAt: Readonly<Record<string, number>>
    tutorGenerating: boolean
}): boolean {
    if (input.tutorGenerating) return false
    if (input.inFlight.has(input.studentId)) return false
    const last = input.lastStartedAt[input.studentId]
    return last == null || input.now - last >= PROPOSAL_COOLDOWN_MS
}

// ── La remédiation du champion ─────────────────────────────────────────────────────────────────

/** Un point de la correction d'un rendu : son type (`erreur`, `vigilance`, `réussite`) et sa notion. */
export type TutorCorrectionPoint = { type: UIActivityCorrectionPointType; notion: string }

/** Au moins deux erreurs… */
const REMEDIATION_MIN_ERRORS = 2

/**
 * La correction dit-elle que l'élève a beaucoup faux ? Au moins deux erreurs, et au moins autant d'erreurs que de
 * réussites : un rendu juste à une erreur près, ou une copie où les réussites l'emportent, n'appelle pas de
 * remédiation (décision prise au câblage, Wajih : « une correction d'activité avec beaucoup d'erreurs, et rien d'autre »).
 */
export function needsRemediation(points: TutorCorrectionPoint[]): boolean {
    const errors = points.filter((point) => point.type === "error").length
    const successes = points.filter((point) => point.type === "success").length
    return errors >= REMEDIATION_MIN_ERRORS && errors >= successes
}

/** « 4 erreurs sur 6 points » : ce que la carte dit de la correction. */
export function remediationCount(points: TutorCorrectionPoint[]): string {
    const errors = points.filter((point) => point.type === "error").length
    return `${countLabel(errors, "erreur")} sur ${countLabel(points.length, "point")}`
}

/** La consigne a son plafond côté serveur (`instructions`, 500 caractères). */
const INSTRUCTIONS_MAX = 500

/**
 * La consigne de la « similaire » de remédiation : une marche plus bas, ciblée sur les notions où l'élève s'est
 * trompé (chacune une fois, dans l'ordre de la correction), sous le plafond du serveur.
 */
export function remediationInstruction(points: TutorCorrectionPoint[]): string {
    const notions: string[] = []
    for (const point of points) {
        const notion = point.notion.trim()
        if (point.type === "error" && notion !== "" && !notions.includes(notion)) notions.push(notion)
    }
    const base = modifierInstruction("easier")
    const target = notions.length > 0 ? ` Cible ce que l'élève a eu faux : ${notions.join(", ")}.` : ""
    return `${base}${target}`.slice(0, INSTRUCTIONS_MAX)
}
